import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';

const PORTAL_HOMES = ['/', '/office', '/office/workspace', '/office/finance', '/office/marketing', '/office/purchasing', '/office/production'];

const MobileBottomNav = ({ items }) => {
  const location = useLocation();
  const displayItems = items.slice(0, 5);
  const count = displayItems.length;
  const containerRef = useRef(null);

  // Find current active index
  const getActiveIndex = useCallback(() => {
    return displayItems.findIndex((item) => {
      const isHome = PORTAL_HOMES.includes(item.path);
      return isHome
        ? location.pathname === item.path
        : (location.pathname === item.path || location.pathname.startsWith(item.path + '/'));
    });
  }, [location.pathname, displayItems]);

  const activeIndex = getActiveIndex();
  const [prevIndex, setPrevIndex] = useState(activeIndex);
  const [animPhase, setAnimPhase] = useState('idle'); // 'idle' | 'lift' | 'move' | 'drop'
  const [ballPos, setBallPos] = useState(activeIndex); // fractional position for smooth anim

  // Detect route change → trigger animation sequence
  useEffect(() => {
    if (activeIndex === prevIndex || activeIndex < 0) return;

    // Phase 1: Lift the ball up
    setAnimPhase('lift');

    const t1 = setTimeout(() => {
      // Phase 2: Move horizontally to new position
      setBallPos(activeIndex);
      setAnimPhase('move');
    }, 80);

    const t2 = setTimeout(() => {
      // Phase 3: Drop down into new notch
      setAnimPhase('drop');
    }, 280);

    const t3 = setTimeout(() => {
      // Done
      setAnimPhase('idle');
      setPrevIndex(activeIndex);
    }, 380);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [activeIndex, prevIndex]);

  // Initialize ball position on mount
  useEffect(() => {
    setBallPos(activeIndex);
    setPrevIndex(activeIndex);
  }, []);

  const notchCenterX = ballPos >= 0 ? ((ballPos + 0.5) / count) * 400 : -200;
  const ballLeftPercent = ballPos >= 0 ? ((ballPos + 0.5) / count) * 100 : -50;

  // Ball vertical position based on animation phase
  const getBallTop = () => {
    switch (animPhase) {
      case 'lift': return '-22px';
      case 'move': return '-22px';
      case 'drop': return '-12px';
      default: return '-12px'; // idle, resting in notch
    }
  };

  // Ball scale based on animation phase
  const getBallScale = () => {
    switch (animPhase) {
      case 'lift': return 'scale(1.1)';
      case 'move': return 'scale(1.05)';
      case 'drop': return 'scale(0.95)';
      default: return 'scale(1)';
    }
  };

  // Icon to show in the floating ball (always the NEW active item)
  const ballIcon = activeIndex >= 0 ? displayItems[activeIndex] : null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden print:hidden">
      <div className="relative" ref={containerRef}>
        
        {/* Solid background bar */}
        <div 
          className="absolute bottom-0 left-0 w-full bg-[#8C6208]"
          style={{ 
            height: '62px',
            boxShadow: '0 -4px 15px rgba(0, 0, 0, 0.1)'
          }}
        ></div>

        {/* Floating active circle with spring animation */}
        {ballIcon && (
          <div
            className="absolute z-20"
            style={{
              left: `${ballLeftPercent}%`,
              transform: `translateX(-50%) ${getBallScale()}`,
              top: getBallTop(),
              transition: animPhase === 'lift' 
                ? 'top 0.08s cubic-bezier(0.4, 0, 1, 1), transform 0.08s ease-out'
                : animPhase === 'move'
                ? 'left 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), top 0.2s ease, transform 0.2s ease'
                : animPhase === 'drop'
                ? 'top 0.1s cubic-bezier(0, 0, 0.2, 1.2), transform 0.1s ease-out'
                : 'all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
          >
            <div className="w-[50px] h-[50px] rounded-full bg-[#F9F5EC] flex items-center justify-center shadow-[0_4px_20px_rgba(0,0,0,0.15)] border-[3px] border-[#8C6208]">
              {(() => {
                const Icon = ballIcon.icon;
                return <Icon strokeWidth={2.2} className="w-5 h-5 text-[#8C6208]" />;
              })()}
            </div>
          </div>
        )}

        {/* Icon buttons row */}
        <div className="relative z-10 flex justify-evenly items-center h-[60px] px-1">
          {displayItems.map((item, index) => {
            const Icon = item.icon;
            const isActive = index === activeIndex;
            // During animation, hide the icon at the destination
            const isAnimatingTo = animPhase !== 'idle' && index === activeIndex;
            const shouldHide = isActive || isAnimatingTo;

            return (
              <Link
                key={index}
                to={item.path}
                className="flex-1 flex items-center justify-center h-full"
              >
                <div 
                  className="flex items-center justify-center"
                  style={{
                    opacity: shouldHide ? 0 : 1,
                    transform: shouldHide ? 'scale(0.5) translateY(-4px)' : 'scale(1) translateY(0)',
                    transition: 'all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    transitionDelay: shouldHide ? '0ms' : '100ms',
                  }}
                >
                  <Icon strokeWidth={1.5} className="w-[21px] h-[21px] text-[#F9F5EC]" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MobileBottomNav;
